import { noop } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { TileSelectionBar, VerticalDivider } from '@/components/DiagramBar';
import { AlignmentPicker } from '@/components/diagrams/components/AlignmentPicker/AlignmentPicker';
import {
  FamilyTreeNode,
  FlowChart,
  FlowChartProps,
} from '@/components/diagrams/FlowChart';
import { Background } from '@/components/diagrams/FlowChart/components/Background';
import { Controls } from '@/components/diagrams/FlowChart/components/Controls';
import { ControlButtonGroup } from '@/components/diagrams/FlowChart/components/Controls/ControlButtonGroup';
import { DownloadButton } from '@/components/diagrams/FlowChart/components/Controls/DownloadButton';
import { EditButton } from '@/components/diagrams/FlowChart/components/Controls/EditButton';
import { FitViewButton } from '@/components/diagrams/FlowChart/components/Controls/FitViewButton';
import { FullscreenButton } from '@/components/diagrams/FlowChart/components/Controls/FullscreenButton';
import { ResetButtonMenuItem } from '@/components/diagrams/FlowChart/components/Controls/ResetButton';
import { ZoomInButton } from '@/components/diagrams/FlowChart/components/Controls/ZoomInButton';
import { ZoomOutButton } from '@/components/diagrams/FlowChart/components/Controls/ZoomOutButton';
import { HelperLines } from '@/components/diagrams/FlowChart/components/HelperLines';
import { useAlignNodes } from '@/components/diagrams/FlowChart/hooks/useAlignNodes';
import { useIsInteractive } from '@/components/diagrams/FlowChart/hooks/useIsInteractive';
import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { MagicWand01Icon } from '@/components/icons/MagicWand01Icon';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { COLORS } from '@/styles/tokens/colors';

import { FamilyTreeVizConfigSettings } from './components/FamilyTreeVizConfigSettings';
import { UnlinkedIndividualsButton } from './components/UnlinkedIndividualsButton';
import { useFamilyTreeContext } from './contexts';
import { useFitViewOnNodesAdded } from './hooks/useFitViewOnNodesAdded';
import { useFlowChartProps } from './hooks/useFlowChartProps.hook';

// Synthetic person tiles are non-interactive layout placeholders
// (pointer-events disabled), so only real people and marriage groups
// participate in align/distribute. The family tree is a flat layout — no node
// ids carry a `:` section prefix — so the shared hook's section awareness
// degenerates to global alignment here.
function isAlignEligibleFamilyTreeNode(node: FamilyTreeNode): boolean {
  if (node.type === 'person') {
    return !node.data.person.isSynthetic;
  }
  return true;
}

export interface FamilyTreeFlowChartProps
  extends Partial<FlowChartProps<FamilyTreeNode>> {
  /** presentationMode is used to disable interactivity with the graph */
  presentationMode?: boolean;
  /** Portal target ref for rendering unlinked individuals button in page header */
  unlinkedButtonPortalRef?: React.RefObject<HTMLDivElement>;
  /** When provided, clicking the Edit button opens the trowser instead of toggling inline edit mode */
  onEditClick?: () => void;
  /** Hide the fullscreen button (used inside the trowser) */
  hideFullscreenButton?: boolean;
}

export const FamilyTreeFlowChart = ({
  presentationMode,
  unlinkedButtonPortalRef,
  onEditClick,
  hideFullscreenButton,
  ...props
}: FamilyTreeFlowChartProps) => {
  const {
    flowChartProps: baseFlowChartProps,
    helperLineHorizontal,
    helperLineVertical,
  } = useFlowChartProps();
  const { state, dispatch, getState } = useFamilyTreeContext();
  const { isInteractive } = useIsInteractive();
  const trackUserEvent = useTrackUserEvent();
  const {
    eligibleCount: alignEligibleCount,
    canAlign,
    canDistribute,
    alignNodes,
    distributeNodes,
  } = useAlignNodes(state.nodes, {
    isEligible: isAlignEligibleFamilyTreeNode,
  });
  const selectedNodeCount = state.nodes.filter((node) => node.selected).length;
  const prevRenderWhiteLabeledRef = useRef(false);

  // Re-fit the view once newly added nodes (e.g. a person added via the
  // relationships sidebar) have been measured, so they are always visible.
  useFitViewOnNodesAdded();

  const onAutoPosition = useCallback(async () => {
    dispatch({ type: 'AUTO_POSITION' });
  }, [dispatch]);

  const getFitViewOptions = useCallback(() => {
    const nodesToFit = state.nodes.filter((node) => {
      if (node.type === 'person') {
        return !node.data.person.isSynthetic;
      }
      return true;
    });

    return {
      nodes: nodesToFit,
    };
  }, [state.nodes]);

  // Close the side panel when the edit button is toggled
  useEffect(() => {
    if (isInteractive && state.isSidePanelOpen) {
      dispatch({ type: 'CLOSE_SIDE_PANEL' });
    }
  }, [dispatch, isInteractive, state.isSidePanelOpen]);

  const flowChartProps: FlowChartProps<FamilyTreeNode> = {
    ...baseFlowChartProps,
    // Disable panning and zooming in presentation mode
    ...(presentationMode && {
      panOnDrag: false,
      zoomOnScroll: false,
      zoomOnDoubleClick: false,
      zoomOnPinch: false,
      zoomActivationKeyCode: null,
      onNodeClick: noop,
      elementsSelectable: false,
    }),
  };

  return (
    <>
      {/* Portal the unlinked individuals button to page header */}
      {unlinkedButtonPortalRef?.current &&
        !presentationMode &&
        createPortal(
          <UnlinkedIndividualsButton />,
          unlinkedButtonPortalRef.current
        )}
      <FlowChart<FamilyTreeNode>
        {...flowChartProps}
        fitViewOptions={getFitViewOptions()}
        nodes={flowChartProps.nodes}
        onNodesChange={flowChartProps.onNodesChange}
        onPaneClick={flowChartProps.onPaneClick}
        panOnDrag={!presentationMode}
        elementsSelectable={!presentationMode}
        nodesDraggable={isInteractive && !presentationMode}
        presentationMode={presentationMode}
        {...props}
      >
        <Background />
        {/* An unused guide canvas can hide diagrams in PDF output. */}
        {isInteractive && !presentationMode && (
          <HelperLines
            vertical={helperLineVertical}
            horizontal={helperLineHorizontal}
          />
        )}
        {!presentationMode && (
          <LuminaryThemeProvider>
            <Controls position="top-left">
              <ButtonWithPopover
                label="Auto tiles"
                variant="secondary"
                buttonSx={{
                  backgroundColor: COLORS.PRIMITIVES.WHITE,
                }}
                size="sm"
                popperProps={{
                  anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
                  transformOrigin: { horizontal: 'left', vertical: 0 },
                }}
                startIcon={MagicWand01Icon}
              >
                <ResetButtonMenuItem onReset={onAutoPosition} />
              </ButtonWithPopover>
              <FamilyTreeVizConfigSettings />
            </Controls>
            {isInteractive && (
              <Controls position="top-center">
                <TileSelectionBar selectedCount={selectedNodeCount}>
                  {canAlign && (
                    <>
                      <VerticalDivider />
                      <AlignmentPicker
                        onAlign={(kind) => {
                          trackUserEvent('family_tree align_clicked', {
                            source: 'family_tree',
                            kind,
                            nodeCount: alignEligibleCount,
                          });
                          alignNodes(kind);
                        }}
                        onDistribute={(axis) => {
                          trackUserEvent('family_tree distribute_clicked', {
                            source: 'family_tree',
                            axis,
                            nodeCount: alignEligibleCount,
                          });
                          distributeNodes(axis);
                        }}
                        canDistribute={canDistribute}
                      />
                    </>
                  )}
                </TileSelectionBar>
              </Controls>
            )}
            <Controls>
              <>
                {onEditClick && !isInteractive && (
                  <EditButton size="sm" onClick={onEditClick} />
                )}
                <DownloadButton
                  viewDisplayName="Family Tree"
                  householdId={state.householdId}
                  tooltipText="Download family tree as PNG"
                  onBeforeDownload={async () => {
                    prevRenderWhiteLabeledRef.current =
                      getState().renderWhiteLabeled;
                    dispatch({
                      type: 'START_EXPORT',
                    });
                    dispatch({
                      type: 'SET_RENDER_WHITE_LABELED',
                      renderWhiteLabeled: true,
                    });
                    // Wait for the family tree to update before exporting the image
                    await new Promise((res) => setTimeout(res, 100));
                  }}
                  onAfterDownload={() => {
                    dispatch({
                      type: 'SET_RENDER_WHITE_LABELED',
                      renderWhiteLabeled: prevRenderWhiteLabeledRef.current,
                    });
                    dispatch({ type: 'END_EXPORT' });
                  }}
                />
                <ControlButtonGroup>
                  {!hideFullscreenButton && (
                    <FullscreenButton name="familyTree" />
                  )}
                  <FitViewButton fitViewOptions={getFitViewOptions} />
                  <ZoomInButton />
                  <ZoomOutButton />
                </ControlButtonGroup>
              </>
            </Controls>
          </LuminaryThemeProvider>
        )}
      </FlowChart>
    </>
  );
};
